import type { TaxonomyField } from '@/entities/taxonomy';

/**
 * One grid column: exactly one schema field.
 *
 * 1:1 with a field, unlike the schema builder's grid, where every field spans
 * TWO sub-columns (its value and its code) and the coordinate space is
 * `fields.length * 2`. That pairing is why the builder's grid cannot simply be
 * reused here — it is baked into its `fieldOfCol` / `subOfCol` arithmetic and
 * into its merged-cell special cases.
 */
export interface GridColumn {
  field: TaxonomyField;
  /** A subfield is indented under its parent and reads as part of that cluster. */
  isSubfield: boolean;
  /**
   * Whether a person types into it. `computed` fields are assembled from a
   * pattern and `linked` fields are pulled from another schema, so neither is
   * something to fill in — they render read-only.
   */
  editable: boolean;
}

/**
 * The columns a record grid shows, in the order the generated name uses.
 *
 * The ordering is `estimateNameLength`'s placement rule, and matching it is not
 * cosmetic: the Preview column is assembled in that order, so a grid that
 * disagreed would show someone a name whose parts appear in a different
 * sequence from the columns they filled.
 *
 * `visible: false` fields are dropped. The property has existed since the schema
 * builder shipped, documented as "shown during submission", with nothing ever
 * reading it — this is the submission it meant.
 */
export function gridColumns(fields: readonly TaxonomyField[]): GridColumn[] {
  const shown = fields.filter((f) => f.visible !== false);

  const topLevel = [
    ...shown.filter((f) => f.group === 'media' && !f.parentId),
    ...shown.filter((f) => f.group === 'content' && !f.parentId),
  ];

  const columns: GridColumn[] = [];
  for (const parent of topLevel) {
    columns.push({ field: parent, isSubfield: false, editable: isEditable(parent) });
    for (const sub of shown.filter((f) => f.parentId === parent.id)) {
      columns.push({ field: sub, isSubfield: true, editable: isEditable(sub) });
    }
  }
  return columns;
}

function isEditable(field: TaxonomyField): boolean {
  return field.type !== 'computed' && field.type !== 'linked';
}
